import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, Modal, Alert } from 'react-bootstrap';
import {
    Send,
    RotateCcw,
    Paperclip,
    FileText,
    Copy,
    ThumbsUp,
    ThumbsDown,
    Check,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Sparkles,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ArtifactRenderer from './artifacts/ArtifactRenderer';
import { io } from 'socket.io-client';
import { useParams, useHistory } from 'react-router-dom';
import { store } from '../../../store/store';
import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

// Generate stable user ID
const generateUserId = (companyId) => {
    const storageKey = `ledgy_chat_user_${companyId}`;
    let userId = localStorage.getItem(storageKey);
    if (!userId) {
        userId = `user-${companyId}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem(storageKey, userId);
    }
    return userId;
};

/**
 * Extract source references from response text.
 * Looks for KÄLLOR: sections or inline URLs.
 */
function extractSources(text) {
    const sources = [];
    // Match "KÄLLOR:" section
    const kallorMatch = text.match(/KÄLLOR:\s*\n([\s\S]*?)(?=\n\n[A-ZÅÄÖ]|\n*$)/);
    if (kallorMatch) {
        const lines = kallorMatch[1].split('\n').filter(l => l.trim().startsWith('-'));
        for (const line of lines) {
            const urlMatch = line.match(/(https?:\/\/[^\s)]+)/);
            const label = line.replace(/^-\s*/, '').replace(urlMatch ? urlMatch[0] : '', '').replace(/:\s*$/, '').trim();
            sources.push({
                label: label || (urlMatch ? new URL(urlMatch[1]).hostname : 'Källa'),
                url: urlMatch ? urlMatch[1] : null,
            });
        }
    }
    // Also find inline URLs not in KÄLLOR section
    const urlRegex = /https?:\/\/(?:www\.)?(?:skatteverket|bfn|riksdagen|bolagsverket|forsakringskassan)\.[a-z]+[^\s)"]*/gi;
    const inlineUrls = text.match(urlRegex) || [];
    for (const url of inlineUrls) {
        if (!sources.some(s => s.url === url)) {
            try {
                sources.push({ label: new URL(url).hostname.replace('www.', ''), url });
            } catch { /* skip invalid */ }
        }
    }
    return sources;
}

/**
 * Strip the KÄLLOR section from displayed text (we show it separately).
 */
function stripSourcesSection(text) {
    return text.replace(/\n*KÄLLOR:\s*\n[\s\S]*?(?=\n\n[A-ZÅÄÖ]|\s*$)/, '').trim();
}

const BookkeepingChatPage = () => {
    const { companyId } = useParams();
    const history = useHistory();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [socket, setSocket] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [pendingVerification, setPendingVerification] = useState(null);
    const [isCreatingVerification, setIsCreatingVerification] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const pageRef = useRef(null);

    // Hide footer and track sidebar width for the fixed input area
    const [sidebarLeft, setSidebarLeft] = useState(0);

    useEffect(() => {
        // Hide footer
        const footer = document.querySelector('.footer');
        if (footer) footer.style.display = 'none';

        // Read sidebar offset from .content-body margin-left
        const updateLeft = () => {
            const cb = document.querySelector('.content-body');
            if (cb) setSidebarLeft(parseInt(getComputedStyle(cb).marginLeft, 10) || 0);
        };
        updateLeft();
        window.addEventListener('resize', updateLeft);

        return () => {
            if (footer) footer.style.display = '';
            window.removeEventListener('resize', updateLeft);
        };
    }, []);

    const userIdRef = useRef(generateUserId(companyId));
    const userId = userIdRef.current;

    const quickStartExamples = [
        { message: "Jag köpte kontorsmaterial för 450 kr med företagskortet" },
        { message: "Vad är prisbasbeloppet för i år?" },
        { message: "Är representation avdragsgillt?" },
        { message: "Vi hade kundlunch för 1200 kr, 3 personer" },
    ];

    // Initialize socket connection
    useEffect(() => {
        const state = store.getState();
        const accessToken = state.auth.accessToken;

        const newSocket = io(process.env.REACT_APP_CHAT_URL || 'http://localhost:3004', {
            withCredentials: true,
            auth: { token: accessToken, userId, companyId }
        });

        newSocket.on('connect', () => {
            setIsConnected(true);
        });

        newSocket.on('disconnect', () => {
            setIsConnected(false);
        });

        newSocket.on('chatResponse', (data) => {
            setIsTyping(false);
            addMessage('bot', data.response, false, {
                verification: data.verification,
                sources: data.sources,
                artifacts: data.artifacts,
                suggestions: data.suggestions,
                requiresMoreInfo: data.requiresMoreInfo
            });
        });

        newSocket.on('chatError', (error) => {
            setIsTyping(false);
            addMessage('bot', error.error + (error.details ? `: ${error.details}` : ''), true);
        });

        newSocket.on('conversationReset', (data) => {
            addMessage('system', data.message);
        });

        setSocket(newSocket);
        setTimeout(() => textareaRef.current?.focus(), 100);

        return () => { newSocket.close(); };
    }, [companyId]);

    // Auto scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
        }
    }, [inputMessage]);

    const addMessage = (sender, message, error = false, extra = {}) => {
        setMessages(prev => [...prev, {
            id: Date.now().toString(),
            sender,
            message,
            timestamp: new Date(),
            error,
            ...extra
        }]);
    };

    const sendMessage = async () => {
        const text = inputMessage.trim();
        if ((!text && attachments.length === 0) || !socket || !isConnected) return;

        addMessage('user', text || '(bild bifogad)');
        setInputMessage('');
        setIsTyping(true);

        const encodedAttachments = await Promise.all(
            attachments.map(att => new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const base64 = reader.result.split(',')[1];
                    resolve({ filename: att.name, mimeType: att.type, data: base64 });
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(att.file);
            }))
        );

        socket.emit('chatMessage', {
            message: text,
            attachments: encodedAttachments.filter(Boolean)
        });
        setAttachments([]);
    };

    const resetConversation = () => {
        if (socket) {
            socket.emit('resetConversation');
            setMessages([]);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            id: Date.now() + Math.random(),
            file,
            name: file.name,
            size: file.size,
            type: file.type
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
    };

    const removeAttachment = (id) => {
        setAttachments(prev => prev.filter(att => att.id !== id));
    };

    const handleVerificationProposal = (verification) => {
        setPendingVerification(verification);
        setShowVerificationModal(true);
    };

    const createVerification = async () => {
        if (!pendingVerification) return;
        setIsCreatingVerification(true);
        try {
            // Use the configured axiosInstance + getApiUrl pipeline so the
            // access token (Redux state.auth.accessToken) is attached by the
            // request interceptor and 401 responses trigger token refresh.
            // The previous direct-fetch path read `localStorage.getItem('token')`
            // (a key nothing writes — auth lives in Redux) and built the URL
            // from REACT_APP_API_URL (a path segment, not a base URL) →
            // every request silently 401'd with Bearer null.
            const url = getApiUrl('verifications');
            const { data: created } = await axiosInstance.post(
                url,
                { companyId, ...pendingVerification },
                { withCredentials: true },
            );
            setShowVerificationModal(false);
            setPendingVerification(null);
            addMessage('system', `Verifikat ${created.id} har skapats.`);
            setTimeout(() => history.push(`/${companyId}/edit-verification/${created.id}`), 2000);
        } catch (error) {
            console.error('Error creating verification:', error);
            addMessage('bot', 'Ett fel uppstod när verifikatet skulle skapas. Försök igen.', true);
        } finally {
            setIsCreatingVerification(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const calculateVerificationTotals = (transactions) => {
        const debit = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
        const credit = transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
        return { debit, credit, balanced: Math.abs(debit - credit) < 0.01 };
    };

    const handleExampleClick = (example) => {
        setInputMessage(example.message);
        textareaRef.current?.focus();
    };

    // ─── Render helpers ─────────────────────────────────────────

    const VISIBLE_SOURCES = 2;

    const SourceChip = ({ source }) => (
        source.url ? (
            <a href={source.url} target="_blank" rel="noopener noreferrer" className="finn-source-chip">
                <ExternalLink size={12} />
                {source.label}
            </a>
        ) : (
            <span className="finn-source-chip">
                <span className="finn-source-dot" />
                {source.label}
            </span>
        )
    );

    const ExpandableSources = ({ sources }) => {
        const [expanded, setExpanded] = useState(false);
        const visible = sources.slice(0, VISIBLE_SOURCES);
        const hidden = sources.slice(VISIBLE_SOURCES);
        const hasMore = hidden.length > 0;

        return (
            <div className="finn-sources">
                <div className="finn-sources-row">
                    <div className="finn-sources-label">Källor</div>
                    <div className="finn-sources-list">
                        {visible.map((s, i) => <SourceChip key={i} source={s} />)}
                        {hasMore && !expanded && (
                            <button
                                className="finn-source-more"
                                onClick={() => setExpanded(true)}
                                title={`Visa ${hidden.length} till`}
                            >
                                +{hidden.length}
                            </button>
                        )}
                    </div>
                </div>
                {hasMore && expanded && (
                    <div className="finn-sources-overflow">
                        <div className="finn-sources-list">
                            {hidden.map((s, i) => <SourceChip key={i + VISIBLE_SOURCES} source={s} />)}
                        </div>
                        <button className="finn-source-less" onClick={() => setExpanded(false)}>
                            Visa färre
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderSources = (structuredSources, text) => {
        const sources = (structuredSources && structuredSources.length > 0)
            ? structuredSources
            : extractSources(text);
        if (sources.length === 0) return null;
        return <ExpandableSources sources={sources} />;
    };

    const renderVerification = (verification) => {
        if (!verification?.transactions?.length) return null;
        const totals = calculateVerificationTotals(verification.transactions);
        return (
            <div className="finn-verification">
                <div className="finn-verification-header">
                    <span>Verifikat</span>
                    <span className="finn-verification-meta">{verification.date} — {verification.description}</span>
                </div>
                <table className="finn-verification-table">
                    <thead>
                        <tr>
                            <th>Konto</th>
                            <th>Beskrivning</th>
                            <th style={{ textAlign: 'right' }}>Debet</th>
                            <th style={{ textAlign: 'right' }}>Kredit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {verification.transactions.map((t, i) => (
                            <tr key={i}>
                                <td className="finn-account">{t.account}</td>
                                <td>{t.description}</td>
                                <td style={{ textAlign: 'right', color: t.amount > 0 ? '#b91c1c' : undefined, fontWeight: t.amount > 0 ? 600 : undefined }}>
                                    {t.amount > 0 ? formatCurrency(t.amount) : ''}
                                </td>
                                <td style={{ textAlign: 'right', color: t.amount < 0 ? '#15803d' : undefined, fontWeight: t.amount < 0 ? 600 : undefined }}>
                                    {t.amount < 0 ? formatCurrency(Math.abs(t.amount)) : ''}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="finn-verification-footer">
                    <span style={{ color: totals.balanced ? '#15803d' : '#b91c1c' }}>
                        {totals.balanced ? '✓ Balanserat' : '⚠ Ej balanserat'}
                    </span>
                    <div className="finn-verification-actions">
                        <Button size="sm" variant="primary" onClick={() => handleVerificationProposal(verification)}>
                            Skapa verifikat
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // ─── Main render ────────────────────────────────────────────

    return (
        <div className="finn-chat-page" ref={pageRef}>
            <style>{`
                /* ── Layout — normal page flow, outer scroll ── */
                .finn-chat-page {
                    background: #fafafa;
                    font-family: var(--font-family-base, 'Inter', sans-serif);
                    min-height: calc(100vh - 7.5rem);
                    padding-bottom: 160px; /* space for fixed input */
                }

                .finn-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.75rem 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                    background: white;
                }

                .finn-header-left {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .finn-avatar {
                    width: 32px;
                    height: 32px;
                    border-radius: 10px;
                    background: var(--primary, #44814E);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.875rem;
                    flex-shrink: 0;
                }

                .finn-header-info h1 {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    margin: 0;
                    color: #111827;
                }

                .finn-header-status {
                    font-size: 0.75rem;
                    color: #6b7280;
                }

                .finn-header-status .dot {
                    display: inline-block;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    margin-right: 4px;
                    vertical-align: middle;
                }

                .finn-header-status .dot.online { background: #22c55e; }
                .finn-header-status .dot.offline { background: #ef4444; }

                .finn-reset-btn {
                    background: none;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.4rem 0.75rem;
                    font-size: 0.8125rem;
                    color: #6b7280;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    transition: all 0.15s;
                }
                .finn-reset-btn:hover {
                    background: #f3f4f6;
                    color: #374151;
                }

                /* ── Messages area ──────────────────────────── */
                .finn-messages-area {
                    padding: 1.5rem 1rem;
                }

                .finn-messages-column {
                    max-width: 720px;
                    margin: 0 auto;
                    width: 100%;
                }

                /* ── Welcome ────────────────────────────────── */
                .finn-welcome {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                    text-align: center;
                    padding: 2rem 1rem;
                }

                .finn-welcome-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    background: var(--primary, #44814E);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 1.25rem;
                }

                .finn-welcome h2 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #111827;
                    margin-bottom: 0.5rem;
                }

                .finn-welcome p {
                    color: #6b7280;
                    font-size: 0.9375rem;
                    line-height: 1.6;
                    max-width: 480px;
                    margin-bottom: 2rem;
                }

                .finn-examples {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    justify-content: center;
                    max-width: 560px;
                }

                .finn-example-btn {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 0.625rem 1rem;
                    font-size: 0.8125rem;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.15s;
                    text-align: left;
                    line-height: 1.4;
                }
                .finn-example-btn:hover {
                    border-color: var(--primary, #44814E);
                    background: var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                    color: var(--primary, #44814E);
                }

                /* ── Message rows ───────────────────────────── */
                .finn-msg {
                    margin-bottom: 1.5rem;
                    animation: finnFadeIn 0.2s ease-out;
                }

                @keyframes finnFadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* User message */
                .finn-msg-user {
                    display: flex;
                    justify-content: flex-end;
                }

                .finn-msg-user-bubble {
                    max-width: 80%;
                    background: var(--rgba-primary-2, rgba(68, 129, 78, 0.15));
                    color: #1a2e1d;
                    padding: 0.75rem 1rem;
                    border-radius: 16px 16px 4px 16px;
                    font-size: 0.9375rem;
                    line-height: 1.55;
                    white-space: pre-wrap;
                }

                .finn-msg-user-time {
                    text-align: right;
                    font-size: 0.6875rem;
                    color: #9ca3af;
                    margin-top: 0.25rem;
                    padding-right: 0.25rem;
                }

                /* Bot message */
                .finn-msg-bot {
                    display: flex;
                    gap: 0.75rem;
                    align-items: flex-start;
                }

                .finn-msg-bot-avatar {
                    width: 28px;
                    height: 28px;
                    border-radius: 8px;
                    background: var(--primary, #44814E);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.75rem;
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .finn-msg-bot-content {
                    flex: 1;
                    min-width: 0;
                }

                .finn-msg-bot-name {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.25rem;
                }

                .finn-msg-bot-text {
                    font-size: 0.9375rem;
                    line-height: 1.65;
                    color: #1f2937;
                }

                /* Markdown styling within bot messages */
                .finn-msg-bot-text p {
                    margin: 0 0 0.75rem;
                }
                .finn-msg-bot-text p:last-child {
                    margin-bottom: 0;
                }
                .finn-msg-bot-text ul,
                .finn-msg-bot-text ol {
                    padding-left: 1.25rem;
                    margin: 0.5rem 0;
                }
                .finn-msg-bot-text li {
                    margin-bottom: 0.25rem;
                }
                .finn-msg-bot-text strong {
                    font-weight: 600;
                    color: #111827;
                }
                .finn-msg-bot-text code {
                    background: #f3f4f6;
                    padding: 0.125rem 0.375rem;
                    border-radius: 4px;
                    font-size: 0.85em;
                }
                .finn-msg-bot-text pre {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    overflow-x: auto;
                    margin: 0.75rem 0;
                }
                .finn-msg-bot-text pre code {
                    background: none;
                    padding: 0;
                }
                .finn-msg-bot-text a {
                    color: var(--primary, #44814E);
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }
                .finn-msg-bot-text table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 0.75rem 0;
                    font-size: 0.875rem;
                }
                .finn-msg-bot-text th,
                .finn-msg-bot-text td {
                    border: 1px solid #e5e7eb;
                    padding: 0.5rem 0.75rem;
                    text-align: left;
                }
                .finn-msg-bot-text th {
                    background: #f9fafb;
                    font-weight: 600;
                }
                .finn-msg-bot-text h1, .finn-msg-bot-text h2, .finn-msg-bot-text h3 {
                    font-size: 1rem;
                    font-weight: 600;
                    margin: 1rem 0 0.5rem;
                    color: #111827;
                }

                /* Bot actions (show on hover) */
                .finn-msg-bot-actions {
                    display: flex;
                    gap: 0.25rem;
                    margin-top: 0.5rem;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .finn-msg-bot-content:hover .finn-msg-bot-actions {
                    opacity: 1;
                }
                .finn-action-btn {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0.25rem;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.75rem;
                    transition: all 0.15s;
                }
                .finn-action-btn:hover {
                    color: #6b7280;
                    background: #f3f4f6;
                }
                .finn-msg-bot-time {
                    font-size: 0.6875rem;
                    color: #9ca3af;
                    margin-left: auto;
                }

                /* System message */
                .finn-msg-system {
                    text-align: center;
                    margin: 1rem 0;
                }
                .finn-msg-system-text {
                    display: inline-block;
                    font-size: 0.8125rem;
                    color: #6b7280;
                    background: #f3f4f6;
                    padding: 0.375rem 0.875rem;
                    border-radius: 10px;
                }

                /* Error message */
                .finn-msg-error .finn-msg-bot-text {
                    color: #b91c1c;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                }

                /* ── Sources ────────────────────────────────── */
                .finn-sources {
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f3f4f6;
                }
                .finn-sources-row {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .finn-sources-label {
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #9ca3af;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    white-space: nowrap;
                }
                .finn-sources-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.375rem;
                    align-items: center;
                }
                .finn-source-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.6875rem;
                    padding: 0.1875rem 0.5rem;
                    border-radius: 999px;
                    background: #f8fafc;
                    color: #64748b;
                    text-decoration: none;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                a.finn-source-chip:hover {
                    background: #f1f5f9;
                    border-color: #94a3b8;
                    color: #334155;
                }
                .finn-source-dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #cbd5e1;
                    flex-shrink: 0;
                }
                .finn-source-more {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.625rem;
                    font-weight: 700;
                    min-width: 28px;
                    height: 22px;
                    padding: 0 0.375rem;
                    border-radius: 999px;
                    background: #e2e8f0;
                    color: #475569;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    letter-spacing: -0.02em;
                }
                .finn-source-more:hover {
                    background: #cbd5e1;
                    color: #1e293b;
                    transform: scale(1.05);
                }
                .finn-sources-overflow {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 0.375rem;
                    margin-top: 0.375rem;
                    padding-left: calc(0.5rem + 3.5em);
                    animation: finnSourcesExpand 0.25s ease-out;
                }
                @keyframes finnSourcesExpand {
                    from { opacity: 0; transform: translateY(-4px); max-height: 0; }
                    to { opacity: 1; transform: translateY(0); max-height: 200px; }
                }
                .finn-source-less {
                    font-size: 0.625rem;
                    color: #94a3b8;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0.125rem 0.25rem;
                    border-radius: 4px;
                    transition: color 0.15s;
                }
                .finn-source-less:hover {
                    color: #475569;
                }

                /* ── Verification card ──────────────────────── */
                .finn-verification {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    margin-top: 0.75rem;
                    overflow: hidden;
                }
                .finn-verification-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.625rem 0.875rem;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                    font-size: 0.8125rem;
                    color: #374151;
                }
                .finn-verification-meta {
                    font-weight: 400;
                    color: #6b7280;
                    font-size: 0.75rem;
                }
                .finn-verification-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.8125rem;
                }
                .finn-verification-table th {
                    padding: 0.5rem 0.875rem;
                    background: #f9fafb;
                    font-weight: 600;
                    color: #374151;
                    border-bottom: 1px solid #e5e7eb;
                    text-align: left;
                }
                .finn-verification-table td {
                    padding: 0.5rem 0.875rem;
                    border-bottom: 1px solid #f3f4f6;
                    color: #374151;
                }
                .finn-account {
                    font-family: 'SF Mono', 'Fira Code', monospace;
                    font-weight: 600;
                    color: #111827;
                }
                .finn-verification-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.625rem 0.875rem;
                    background: #f9fafb;
                    border-top: 1px solid #e5e7eb;
                    font-size: 0.8125rem;
                    font-weight: 600;
                }
                .finn-verification-actions {
                    display: flex;
                    gap: 0.375rem;
                }

                /* ── Artifacts (financial tables, KPIs, charts, invoice lists) ─── */
                .finn-artifacts {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                    margin-top: 0.75rem;
                }
                .finn-artifact {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    overflow: hidden;
                    animation: finnFadeIn 0.3s ease-out;
                }
                .finn-artifact-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.5rem 0.875rem;
                    background: #f8fafc;
                    border-bottom: 1px solid #e5e7eb;
                }
                .finn-artifact-title {
                    font-weight: 600;
                    font-size: 0.8125rem;
                    color: #1e293b;
                }
                .finn-artifact-period {
                    font-size: 0.6875rem;
                    color: #94a3b8;
                    font-weight: 500;
                }
                .finn-artifact-total {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #1e293b;
                    font-variant-numeric: tabular-nums;
                }

                /* Financial Table */
                .finn-financial-table table,
                .finn-invoice-list table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.8125rem;
                }
                .finn-financial-table th,
                .finn-invoice-list th {
                    padding: 0.4375rem 0.875rem;
                    background: #f8fafc;
                    font-weight: 600;
                    font-size: 0.75rem;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    border-bottom: 1px solid #e5e7eb;
                }
                .finn-financial-table td,
                .finn-invoice-list td {
                    padding: 0.4375rem 0.875rem;
                    border-bottom: 1px solid #f1f5f9;
                    color: #334155;
                }
                .finn-financial-table tbody tr:hover,
                .finn-invoice-list tbody tr:hover {
                    background: #f8fafc;
                }
                .finn-num {
                    font-variant-numeric: tabular-nums;
                    font-feature-settings: 'tnum';
                }
                .finn-num-mono {
                    font-family: 'SF Mono', 'Fira Code', monospace;
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.75rem;
                }
                .finn-negative {
                    color: #b91c1c;
                }
                .finn-totals-row td {
                    font-weight: 700;
                    color: #0f172a;
                    border-top: 2px solid #cbd5e1;
                    border-bottom: none;
                    background: #f8fafc;
                }
                .finn-table-scroll {
                    overflow-x: auto;
                }

                /* KPI Grid */
                .finn-kpi-cards {
                    display: grid;
                    gap: 0.5rem;
                    padding: 0.75rem;
                }
                .finn-kpi-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem;
                    text-align: center;
                }
                .finn-kpi-label {
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin-bottom: 0.25rem;
                }
                .finn-kpi-value {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: #0f172a;
                    font-variant-numeric: tabular-nums;
                    line-height: 1.2;
                }
                .finn-kpi-trend {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.25rem;
                    margin-top: 0.375rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                .finn-kpi-trend-up { color: #15803d; }
                .finn-kpi-trend-down { color: #b91c1c; }
                .finn-kpi-trend-flat { color: #64748b; }
                .finn-kpi-trend-label {
                    font-weight: 400;
                    color: #94a3b8;
                }

                /* Invoice badges */
                .finn-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.125rem;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    padding: 0.125rem 0.5rem;
                    border-radius: 4px;
                }
                .finn-badge-paid {
                    background: #dcfce7;
                    color: #15803d;
                }
                .finn-badge-unpaid {
                    background: #fef3c7;
                    color: #92400e;
                }
                .finn-badge-overdue {
                    background: #fee2e2;
                    color: #b91c1c;
                }
                .finn-badge-pending {
                    background: #e0e7ff;
                    color: #3730a3;
                }
                .finn-badge-days {
                    font-weight: 400;
                }
                .finn-row-overdue {
                    background: #fef2f2 !important;
                }

                /* Bar Chart */
                .finn-chart-body {
                    padding: 0.75rem;
                    display: flex;
                    gap: 0.375rem;
                    align-items: flex-end;
                }
                .finn-chart-ylabel {
                    writing-mode: vertical-rl;
                    transform: rotate(180deg);
                    font-size: 0.625rem;
                    color: #94a3b8;
                    text-align: center;
                    padding-bottom: 1.25rem;
                }
                .finn-chart-bars {
                    flex: 1;
                    display: flex;
                    align-items: flex-end;
                    gap: 0.25rem;
                    height: 140px;
                }
                .finn-chart-column {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 0;
                }
                .finn-chart-bar-group {
                    display: flex;
                    gap: 2px;
                    align-items: flex-end;
                    width: 100%;
                    height: 120px;
                }
                .finn-chart-bar {
                    flex: 1;
                    border-radius: 3px 3px 0 0;
                    transition: height 0.4s ease-out;
                    min-height: 2px;
                    cursor: default;
                }
                .finn-chart-bar-primary {
                    background: #3b82f6;
                }
                .finn-chart-bar-secondary {
                    background: #f59e0b;
                }
                .finn-chart-bar:hover {
                    opacity: 0.8;
                }
                .finn-chart-label {
                    font-size: 0.625rem;
                    color: #64748b;
                    margin-top: 0.25rem;
                    text-align: center;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }
                .finn-chart-legend {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    padding: 0.5rem;
                    font-size: 0.6875rem;
                    color: #64748b;
                }
                .finn-chart-legend-item {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                .finn-chart-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 2px;
                }
                .finn-chart-dot-primary { background: #3b82f6; }
                .finn-chart-dot-secondary { background: #f59e0b; }

                /* ── Typing indicator ───────────────────────── */
                .finn-typing {
                    display: flex;
                    gap: 0.75rem;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }
                .finn-typing-dots {
                    display: flex;
                    gap: 4px;
                    padding-top: 0.5rem;
                }
                .finn-typing-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #9ca3af;
                    animation: finnBounce 1.4s infinite ease-in-out;
                }
                .finn-typing-dot:nth-child(1) { animation-delay: -0.32s; }
                .finn-typing-dot:nth-child(2) { animation-delay: -0.16s; }
                .finn-typing-dot:nth-child(3) { animation-delay: 0s; }

                @keyframes finnBounce {
                    0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
                    40% { transform: scale(1); opacity: 1; }
                }

                .finn-typing-label {
                    font-size: 0.8125rem;
                    color: #9ca3af;
                    padding-top: 0.375rem;
                }

                /* ── Input area (fixed to bottom, aligned with content area) */
                .finn-input-area {
                    position: fixed;
                    bottom: 0;
                    right: 0;
                    z-index: 10;
                    background: #fafafa;
                    border-top: 1px solid #e5e7eb;
                    padding: 0.75rem 1rem 1rem;
                }

                .finn-input-column {
                    max-width: 720px;
                    margin: 0 auto;
                }

                .finn-attachments {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.375rem;
                    margin-bottom: 0.5rem;
                }
                .finn-attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.3rem 0.625rem;
                    font-size: 0.75rem;
                    color: #4b5563;
                }
                .finn-attachment-remove {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0;
                    font-size: 1rem;
                    line-height: 1;
                    margin-left: 0.25rem;
                }
                .finn-attachment-remove:hover { color: #ef4444; }

                .finn-input-row {
                    display: flex;
                    align-items: flex-end;
                    gap: 0.5rem;
                }

                .finn-input-box {
                    flex: 1;
                    background: #f9fafb;
                    border: 1.5px solid #e5e7eb;
                    border-radius: 14px;
                    padding: 0;
                    display: flex;
                    align-items: flex-end;
                    transition: border-color 0.15s;
                    overflow: hidden;
                }
                .finn-input-box:focus-within {
                    border-color: var(--primary, #44814E);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                }

                .finn-textarea {
                    flex: 1;
                    border: none;
                    outline: none;
                    resize: none;
                    padding: 0.875rem 1rem;
                    font-size: 0.9375rem;
                    line-height: 1.5;
                    background: transparent;
                    color: #1f2937;
                    min-height: 64px;
                    max-height: 150px;
                }
                .finn-textarea::placeholder { color: #9ca3af; }

                .finn-input-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.125rem;
                    padding: 0.375rem 0.5rem;
                }

                .finn-input-icon-btn {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0.375rem;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    transition: all 0.15s;
                }
                .finn-input-icon-btn:hover:not(:disabled) {
                    color: #6b7280;
                    background: #e5e7eb;
                }
                .finn-input-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

                .finn-send-btn {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    border: none;
                    background: var(--primary, #44814E);
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s;
                    flex-shrink: 0;
                }
                .finn-send-btn:hover:not(:disabled) {
                    background: var(--primary-hover, #32603a);
                }
                .finn-send-btn:disabled {
                    background: #d1d5db;
                    cursor: not-allowed;
                }

                .finn-disclaimer {
                    text-align: center;
                    margin-top: 0.5rem;
                    font-size: 0.6875rem;
                    color: #9ca3af;
                }
            `}</style>

            {/* Header */}
            <div className="finn-header">
                <div className="finn-header-left">
                    <div className="finn-avatar">F</div>
                    <div className="finn-header-info">
                        <h1>Finn</h1>
                        <div className="finn-header-status">
                            <span className={`dot ${isConnected ? 'online' : 'offline'}`}></span>
                            {isConnected ? 'Online' : 'Ansluter...'}
                        </div>
                    </div>
                </div>
                <button className="finn-reset-btn" onClick={resetConversation}>
                    <RotateCcw size={14} />
                    Ny chatt
                </button>
            </div>

            {/* Messages */}
            <div className="finn-messages-area">
                <div className="finn-messages-column">
                    {/* Welcome screen */}
                    {messages.length === 0 && (
                        <div className="finn-welcome">
                            <div className="finn-welcome-icon">F</div>
                            <h2>Hej, jag är Finn!</h2>
                            <p>
                                Din bokföringsassistent. Beskriv en transaktion, ladda upp ett kvitto,
                                eller ställ en fråga om bokföring och skatteregler.
                            </p>
                            <div className="finn-examples">
                                {quickStartExamples.map((ex, i) => (
                                    <button key={i} className="finn-example-btn" onClick={() => handleExampleClick(ex)}>
                                        {ex.message}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    {messages.map((msg) => {
                        if (msg.sender === 'system') {
                            return (
                                <div key={msg.id} className="finn-msg finn-msg-system">
                                    <span className="finn-msg-system-text">{msg.message}</span>
                                </div>
                            );
                        }

                        if (msg.sender === 'user') {
                            return (
                                <div key={msg.id} className="finn-msg">
                                    <div className="finn-msg-user">
                                        <div className="finn-msg-user-bubble">{msg.message}</div>
                                    </div>
                                    <div className="finn-msg-user-time">
                                        {msg.timestamp.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            );
                        }

                        // Bot message
                        const displayText = stripSourcesSection(msg.message || '');
                        return (
                            <div key={msg.id} className={`finn-msg finn-msg-bot ${msg.error ? 'finn-msg-error' : ''}`}>
                                <div className="finn-msg-bot-avatar">F</div>
                                <div className="finn-msg-bot-content">
                                    <div className="finn-msg-bot-name">Finn</div>
                                    <div className="finn-msg-bot-text">
                                        <ReactMarkdown>{displayText}</ReactMarkdown>
                                    </div>

                                    {/* Structured artifacts (tables, KPIs, charts) */}
                                    {msg.artifacts?.length > 0 && (
                                        <div className="finn-artifacts">
                                            {msg.artifacts.map((artifact, i) => (
                                                <ArtifactRenderer key={artifact.id || i} artifact={artifact} />
                                            ))}
                                        </div>
                                    )}

                                    {/* Verification card */}
                                    {msg.verification && renderVerification(msg.verification)}

                                    {/* Source references */}
                                    {!msg.error && renderSources(msg.sources, msg.message || '')}

                                    {/* Actions (hover) */}
                                    {!msg.error && (
                                        <div className="finn-msg-bot-actions">
                                            <button className="finn-action-btn" onClick={() => copyToClipboard(msg.message, msg.id)}>
                                                {copiedId === msg.id ? <Check size={12} /> : <Copy size={12} />}
                                                {copiedId === msg.id ? 'Kopierat' : ''}
                                            </button>
                                            <button className="finn-action-btn"><ThumbsUp size={12} /></button>
                                            <button className="finn-action-btn"><ThumbsDown size={12} /></button>
                                            <span className="finn-msg-bot-time">
                                                {msg.timestamp.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing */}
                    {isTyping && (
                        <div className="finn-typing">
                            <div className="finn-msg-bot-avatar">F</div>
                            <div>
                                <div className="finn-typing-dots">
                                    <div className="finn-typing-dot"></div>
                                    <div className="finn-typing-dot"></div>
                                    <div className="finn-typing-dot"></div>
                                </div>
                                <div className="finn-typing-label">Finn tänker...</div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            <div className="finn-input-area" style={{ left: sidebarLeft }}>
                <div className="finn-input-column">
                    {/* Attachments */}
                    {attachments.length > 0 && (
                        <div className="finn-attachments">
                            {attachments.map((att) => (
                                <div key={att.id} className="finn-attachment-chip">
                                    <FileText size={12} />
                                    <span>{att.name}</span>
                                    <span style={{ color: '#9ca3af' }}>({formatFileSize(att.size)})</span>
                                    <button className="finn-attachment-remove" onClick={() => removeAttachment(att.id)}>×</button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="finn-input-row">
                        <div className="finn-input-box">
                            <textarea
                                ref={textareaRef}
                                className="finn-textarea"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Skriv ett meddelande..."
                                disabled={!isConnected}
                                rows={1}
                            />
                            <div className="finn-input-controls">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    accept="image/*,.pdf"
                                />
                                <button
                                    className="finn-input-icon-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!isConnected}
                                >
                                    <Paperclip size={16} />
                                </button>
                            </div>
                        </div>
                        <button
                            className="finn-send-btn"
                            onClick={sendMessage}
                            disabled={(!inputMessage.trim() && attachments.length === 0) || !isConnected}
                        >
                            <Send size={18} />
                        </button>
                    </div>

                    <div className="finn-disclaimer">
                        Finn kan göra misstag — kontrollera viktiga uppgifter
                    </div>
                </div>
            </div>

            {/* Verification Modal */}
            <Modal show={showVerificationModal} onHide={() => setShowVerificationModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Skapa verifikat</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pendingVerification && (
                        <>
                            <Alert variant="info">Granska verifikatet nedan innan det skapas i systemet.</Alert>
                            <div className="mb-3">
                                <strong>Datum:</strong> {pendingVerification.date}<br />
                                <strong>Beskrivning:</strong> {pendingVerification.description}
                            </div>
                            {pendingVerification.transactions && (
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Konto</th>
                                            <th>Beskrivning</th>
                                            <th className="text-end">Debet</th>
                                            <th className="text-end">Kredit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingVerification.transactions.map((t, i) => (
                                            <tr key={i}>
                                                <td><span className="finn-account">{t.account}</span></td>
                                                <td>{t.description}</td>
                                                <td className="text-end" style={{ color: t.amount > 0 ? '#b91c1c' : undefined }}>
                                                    {t.amount > 0 ? formatCurrency(t.amount) : '—'}
                                                </td>
                                                <td className="text-end" style={{ color: t.amount < 0 ? '#15803d' : undefined }}>
                                                    {t.amount < 0 ? formatCurrency(Math.abs(t.amount)) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowVerificationModal(false)} disabled={isCreatingVerification}>
                        Avbryt
                    </Button>
                    <Button variant="primary" onClick={createVerification} disabled={isCreatingVerification}>
                        {isCreatingVerification ? 'Skapar...' : 'Skapa verifikat'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BookkeepingChatPage;
